import {
	FeatureServiceBinder,
	FeatureServiceProviderDefinition,
	FeatureServices,
	SharedFeatureService,
} from '@feature-hub/core';
import { AuthServiceProviderV2, IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { ServiceConfigProviderV1 } from '@volkswagen-onehub/service-config-provider';
import { IProfileServiceV2, ProfileServiceV2Impl, UserService } from '@vwdfive/myvw-services';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { acceptPerson, consentsPerson, createPerson, getPerson, unsuscribePerson } from './api/person-api';
import { confirmVehicle, createVehicle, deleteVehicle, getVehicles } from './api/vehicles-api';
import { IEsOnehubVWConfig } from './models/es-one-hub-config';
import { GetPersonUser, IdentityKitUser, PortalPersonStatus } from './models/portal';
import { ApiResponse } from './models/api-response';
import { IdentityKitCompleteMatchForm } from './models/users';
import { DataVehicles, Order, Vehicle } from './models/vehicles';
import { PortalServiceV1 } from './v1';
import { getDataCollection, newAnswerDataCollection } from './api/data-collection-api';
import { DataCollection } from './models/dataCollection';
import { getMaintenanceDetail, getMaintenanceNext, getMaintenanceVehicle } from './api/maintenance-plan-api';
import { MaintenanceDetail, MaintenanceNext, MaintenanceVehicle } from './models/maintenance-plan';
import { participatePromotion } from './api/promotions-api';
import { getBenefits } from './api/benefits-api';
import { PersonBenefits, VWDataVouchers, SalesforceVouchers } from './models/benefits';

export interface SharedIdentityMatchFeatureService extends SharedFeatureService {
	readonly '1.0.0': FeatureServiceBinder<PortalServiceV1>;
}

export enum IdentityMatchStatus {
	OK = 'OK',
	PENDING = 'PENDING',
}

export interface LayerDependencies extends FeatureServices {
	readonly 'vw:authService': AuthServiceProviderV2;
	readonly 'service-config-provider': ServiceConfigProviderV1;
}

export const portalFeatureServiceDefinition: FeatureServiceProviderDefinition<SharedIdentityMatchFeatureService> = {
	//id: 'portal-feature-service',
	id: 'es:portal-feature-service',
	dependencies: {
		featureServices: {
			'service-config-provider': '1.0.0',
		},
	},
	optionalDependencies: {
		featureServices: {
			['vw:authService']: '2.0.0',
		},
	},

	create: ({ featureServices }) => {
		const serviceConfigProvider = featureServices['service-config-provider'] as ServiceConfigProviderV1;
		const authServiceBuilder = featureServices['vw:authService'] as AuthServiceProviderV2 | undefined;
		const authServiceMarketFlag: IAuthServiceV2 | null = authServiceBuilder?.register('market') || null;

		const profileService =
			authServiceMarketFlag && serviceConfigProvider.configs['customer-profile']
				? new ProfileServiceV2Impl(authServiceMarketFlag, serviceConfigProvider.configs['customer-profile'].urlOrigin)
				: null;
		const userService = authServiceMarketFlag ? new UserService(authServiceMarketFlag) : null;

		const esOnehubVWConfig = serviceConfigProvider.configs['es-onehub-vw']?.customConfig as IEsOnehubVWConfig;

		const personInfo: BehaviorSubject<GetPersonUser | null> = new BehaviorSubject<GetPersonUser | null>(null);

		const userIdentityKitLogged: BehaviorSubject<IdentityKitUser> = new BehaviorSubject<IdentityKitUser>({
			identityKitId: null,
			email: null,
			firstName: null,
			lastName: null,
			salutation: null,
			academicTitle: null,
			phoneNumber: null,
			phoneNumbers: null,
		} as IdentityKitUser);

		const needsLogin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
		const validatingIDKit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

		const personVehicles: BehaviorSubject<DataVehicles | null> = new BehaviorSubject<DataVehicles | null>(null);

		const personDataCollection: BehaviorSubject<DataCollection | null> = new BehaviorSubject<DataCollection | null>(
			null,
		);

		const personBenefits: BehaviorSubject<PersonBenefits | null> = new BehaviorSubject<PersonBenefits | null>(null);

		const portalFeatureService_1_0_0 = {
			featureService: {
				//Gestion de Login
				getPerson: (): BehaviorSubject<GetPersonUser | null> => {
					if (!authServiceMarketFlag || !profileService || !userService) {
						return personInfo;
					}

					if (!personInfo.getValue()) {
						portalFeatureService_1_0_0.featureService
							.isAuthenticated(authServiceMarketFlag, profileService, userService)
							.then((identityKit) => {
								if (identityKit && identityKit.identityKitId && authServiceMarketFlag) {
									getPerson(authServiceMarketFlag, esOnehubVWConfig)
										.then((response) => {
											personInfo.next({
												identityKitUser: identityKit,
												portalUser: response as ApiResponse<PortalPersonStatus>,
											});
											console.log('🚀 ~ .then ~ personInfo', personInfo.getValue());
										})
										.catch((err) => {
											personInfo.error(err);
										});
								}
							});
					}

					return personInfo;
				},

				isAuthenticated: async (
					authServiceInstance: IAuthServiceV2 | null,
					profileService: IProfileServiceV2 | null,
					userService: UserService | null,
				): Promise<IdentityKitUser | undefined> => {
					if (!authServiceInstance || !profileService || !userService) {
						return undefined;
					}

					validatingIDKit.next(true);
					const logged = await authServiceInstance.isAuthenticated();
					if (logged) {
						needsLogin.next(false);
						const userProfile = await profileService.getUserProfile();
						const email = await userService.getCurrentUserEmail();
						const identityKitId = await userService.getCurrentUserId();

						const identityKitUser = {
							...userProfile,
							email: email,
							identityKitId: identityKitId,
						} as IdentityKitUser;

						userIdentityKitLogged.next(identityKitUser);
						validatingIDKit.next(false);

						console.log('🚀 ~ App ~ identityKitUser', identityKitUser);
						return identityKitUser;
					} else {
						validatingIDKit.next(false);
						needsLogin.next(true);
					}
					return;
				},

				isValidatingIDKit: (): BehaviorSubject<boolean> => validatingIDKit,
				hasToLog: (): BehaviorSubject<boolean> => needsLogin,

				createPerson: (
					formValues: IdentityKitCompleteMatchForm,
					perfiladoComercial: string,
					device: string,
					tc: string,
					url: string,
				): Observable<GetPersonUser | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						createPerson(authServiceMarketFlag, esOnehubVWConfig, {
							personalData: {
								name: formValues?.name || '',
								firstSurname: formValues?.surname1 || '',
								secondSurname: formValues?.surname2 || '',
								documentType: formValues?.typeDocument || '',
								documentValue: formValues?.document || '',
								email: personInfo.getValue()?.identityKitUser.email as string,
								//identitykitId: personInfo.getValue()?.identityKitUser.identityKitId as string,
							},
							clubVWData: {
								perfiladoComercial: perfiladoComercial,
								originDeviceType: device,
								originTrackingCode: tc,
								formUrl: url,
							},
						})
							.then((createdPerson) => {
								return portalFeatureService_1_0_0.featureService
									.isAuthenticated(authServiceMarketFlag, profileService, userService)
									.then((identityKit) => {
										if (identityKit && identityKit.identityKitId) {
											const updated = {
												identityKitUser: identityKit,
												portalUser: createdPerson,
											};
											personInfo.next(updated);
											return updated;
										} else {
											return null;
										}
									});
							})
							.catch((error) => {
								console.log('error', error);
								personInfo.next(null);
								throw error;
							}),
					);
				},

				acceptPerson: (
					perfiladoComercial: string,
					device: string,
					tc: string,
					url: string,
				): Observable<GetPersonUser | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						acceptPerson(authServiceMarketFlag, esOnehubVWConfig, {
							clubVWData: {
								perfiladoComercial: perfiladoComercial,
								originDeviceType: device,
								originTrackingCode: tc,
								formUrl: url,
							},
						})
							.then((acceptedPerson) => {
								return portalFeatureService_1_0_0.featureService
									.isAuthenticated(authServiceMarketFlag, profileService, userService)
									.then((identityKit) => {
										if (identityKit && identityKit.identityKitId) {
											const updated = {
												identityKitUser: identityKit,
												portalUser: acceptedPerson,
											};
											personInfo.next(updated);
											return updated;
										} else {
											return null;
										}
									});
							})
							.catch((error) => {
								console.log(error);
								personInfo.next(null);
								throw error;
							}),
					);
				},

				//Consentimietos de la persona
				consentsPerson: (notificaciones: boolean): Observable<GetPersonUser | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						consentsPerson(authServiceMarketFlag, esOnehubVWConfig, {
							notificaciones: notificaciones,
						})
							.then((consentsPersonData) => {
								const currentPerson = personInfo.getValue();
								if (currentPerson) {
									const updated = {
										identityKitUser: currentPerson.identityKitUser,
										portalUser: consentsPersonData,
									};
									personInfo.next(updated);
									return updated;
								} else {
									return null;
								}
							})
							.catch((error) => {
								throw error;
							}),
					);
				},

				//Baja del club volkswagen
				unsuscribePerson: (): Observable<GetPersonUser | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						unsuscribePerson(authServiceMarketFlag, esOnehubVWConfig)
							.then((unsuscribedPerson) => {
								return portalFeatureService_1_0_0.featureService
									.isAuthenticated(authServiceMarketFlag, profileService, userService)
									.then((identityKit) => {
										if (identityKit && identityKit.identityKitId) {
											const updated = {
												identityKitUser: identityKit,
												portalUser: unsuscribedPerson,
											};
											personInfo.next(updated);
											return updated;
										} else {
											return null;
										}
									});
							})
							.catch((error) => {
								personInfo.next(null);
								throw error;
							}),
					);
				},

				//Gestion de vehiculos
				getPersonVehicles: (): BehaviorSubject<DataVehicles | null> => {
					if (!authServiceMarketFlag) {
						return personVehicles;
					}
					if (!personVehicles.getValue() && !personVehicles.observed) {
						const identityKitUser = userIdentityKitLogged.getValue().identityKitId;

						if (identityKitUser) {
							(async (): Promise<DataVehicles> => {
								const vehicles = (await getVehicles(authServiceMarketFlag, esOnehubVWConfig))?.data;
								return vehicles;
							})()
								.then((userVehicles) => {
									personVehicles.next(userVehicles);
								})
								.catch((err) => {
									personVehicles.error(err);
								});
						}
					}
					return personVehicles;
				},

				getVehicleOrOrderByVin: (vin: string): BehaviorSubject<Vehicle | Order | null> => {
					const vehicleByVin = new BehaviorSubject<Vehicle | Order | null>(null);

					const vehicles = personVehicles.getValue();
					if (vehicles) {
						const foundVehicle = vehicles.vehicles.find((vehicle) => vehicle.name === vin);
						const foundOrder = vehicles.orders?.find((order) => order.rcyVin === vin);
						vehicleByVin.next(foundVehicle || foundOrder || null);
					}

					return vehicleByVin;
				},

				//Gestion de coches journey
				confirmPersonVehicle: (vin: string): Observable<DataVehicles | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						confirmVehicle(authServiceMarketFlag, esOnehubVWConfig, vin).then((response) => {
							const updatedVehicles = response?.data;
							personVehicles.next(updatedVehicles);
							return updatedVehicles;
						}),
					);
				},

				deletePersonVehicle: (vin: string): Observable<DataVehicles | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						deleteVehicle(authServiceMarketFlag, esOnehubVWConfig, vin).then((response) => {
							const updatedVehicles = response?.data;
							personVehicles.next(updatedVehicles); // ← Estado global
							return updatedVehicles; // ← Lo que emite from()
						}),
					);
				},

				createPersonVehicle: (
					vin: string,
					registrationPlate: string,
					fichaTecnica: File,
					permisoCirculacion: File,
				): Observable<DataVehicles | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						createVehicle(
							authServiceMarketFlag,
							esOnehubVWConfig,
							vin,
							registrationPlate,
							fichaTecnica,
							permisoCirculacion,
						).then((response) => {
							const createdVehicle = response?.data;
							personVehicles.next(createdVehicle); // ← Estado global
							return createdVehicle; // ← Lo que emite el Observable
						}),
					);
				},

				//Gestion de Data collection
				getPersonDataCollection: (): BehaviorSubject<DataCollection | null> => {
					if (!authServiceMarketFlag) {
						return personDataCollection;
					}
					if (!personDataCollection.getValue() && !personDataCollection.observed) {
						const identityKitUser = userIdentityKitLogged.getValue().identityKitId;

						if (identityKitUser) {
							(async (): Promise<DataCollection> => {
								const dataCollection = (await getDataCollection(authServiceMarketFlag, esOnehubVWConfig))?.data;
								return dataCollection;
							})().then((data) => {
								personDataCollection.next(data);
							});
						}
					}
					return personDataCollection;
				},

				//Gestion de new answer Data collection
				newPersonAnswerDataCollection: (questionAnswer: string[]): Observable<DataCollection | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						newAnswerDataCollection(authServiceMarketFlag, esOnehubVWConfig, questionAnswer).then((response) => {
							const updatedDataCollection = response?.data;
							personDataCollection.next(updatedDataCollection);
							return updatedDataCollection;
						}),
					);
				},

				////////////////////////////
				//Llamadas mantenimientos//
				////////////////////////////
				getMaintenanceVehicle: async (vin: string): Promise<MaintenanceVehicle> => {
					if (!authServiceMarketFlag) {
						throw new Error('Auth service not available');
					}
					const maintenanceVehicle = (await getMaintenanceVehicle(authServiceMarketFlag, esOnehubVWConfig, vin))?.data;
					return maintenanceVehicle;
				},

				getMaintenanceNext: async (vin: string): Promise<MaintenanceNext> => {
					if (!authServiceMarketFlag) {
						throw new Error('Auth service not available');
					}
					const maintenanceNext = (await getMaintenanceNext(authServiceMarketFlag, esOnehubVWConfig, vin))?.data;
					return maintenanceNext;
				},

				getMaintenanceDetail: async (vin: string, orCode: string): Promise<MaintenanceDetail> => {
					if (!authServiceMarketFlag) {
						throw new Error('Auth service not available');
					}
					const maintenanceDetail = (await getMaintenanceDetail(authServiceMarketFlag, esOnehubVWConfig, vin, orCode))
						?.data;
					return maintenanceDetail;
				},

				/////////////////////
				//Llamadas Benefits//
				/////////////////////
				getPersonBenefits: (): BehaviorSubject<PersonBenefits | null> => {
					if (!authServiceMarketFlag) {
						return personBenefits;
					}
					if (!personBenefits.getValue()) {
						(async (): Promise<PersonBenefits> => {
							return (await getBenefits(authServiceMarketFlag, esOnehubVWConfig))?.data;
						})().then((benefits) => {
							personBenefits.next(benefits);
						});
					}
					return personBenefits;
				},

				getVoucherDetail: async (voucherId: string): Promise<VWDataVouchers | SalesforceVouchers | null> => {
					if (!authServiceMarketFlag) {
						return null;
					}
					let benefits = personBenefits.getValue();

					if (!benefits) {
						benefits = (await getBenefits(authServiceMarketFlag, esOnehubVWConfig))?.data;
						personBenefits.next(benefits);
					}

					return (
						benefits.benefits.salesforceVouchers.find((vouchers) => vouchers.id === voucherId) ??
						benefits.benefits.vwDataVouchers.find((vouchers) => vouchers.id === voucherId) ??
						null
					);
				},

				///////////////////////
				//Llamadas promotions//
				///////////////////////
				postParticipate: (promotionData: any): Observable<PersonBenefits | null> => {
					if (!authServiceMarketFlag) {
						return new Observable((subscriber) => {
							subscriber.next(null);
							subscriber.complete();
						});
					}

					return from(
						participatePromotion(authServiceMarketFlag, esOnehubVWConfig, promotionData).then((apiResponse) => {
							const updatedBenefits = apiResponse.data ?? null;
							personBenefits.next(updatedBenefits);
							return updatedBenefits;
						}),
					);
				},
			},
		};
		return {
			'1.0.0': () => portalFeatureService_1_0_0,
		};
	},
};
